<script setup>
import { computed, ref, reactive, nextTick } from "vue";
import { useRouter } from "vue-router";
import { Modal } from "bootstrap";
import MainLayout from "@/components/layout/MainLayout.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import ArrearsStatCard from "@/components/arrears/ArrearsStatCard.vue";
import ArrearsFilterBar from "@/components/arrears/ArrearsFilterBar.vue";
import AgingSummaryCard from "@/components/arrears/AgingSummaryCard.vue";
import ArrearsTable from "@/components/arrears/ArrearsTable.vue";
import { useArrearsStore } from "@/stores/arrears";
import { useInvoiceStore } from "@/stores/invoice";

const router = useRouter();
const arrearsStore = useArrearsStore();
const invoiceStore = useInvoiceStore();
const reminderModalEl = ref(null);
const reminderForm = reactive({ includeSMS: true, includeEmail: true });

const search = computed({
  get: () => arrearsStore.filters.search,
  set: (v) => arrearsStore.setSearch(v),
});
const agingGroup = computed({
  get: () => arrearsStore.filters.agingGroup,
  set: (v) => arrearsStore.setAgingGroup(v),
});
const contactStatus = computed({
  get: () => arrearsStore.filters.contactStatus,
  set: (v) => arrearsStore.setContactStatus(v),
});

const formatCurrency = (v) =>
  new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 0,
  }).format(v || 0);

const getModal = () => Modal.getOrCreateInstance(reminderModalEl.value);

const confirmSendReminder = () => {
  const channels = [];
  if (reminderForm.includeSMS) channels.push("SMS");
  if (reminderForm.includeEmail) channels.push("Email");
  getModal().hide();

  setTimeout(() => {
    window.alert(
      `ส่งแจ้งเตือนผ่าน ${channels.join(" และ ")} ไปยัง ${
        arrearsStore.totalCases
      } รายการสำเร็จ`,
    );
  }, 300);
};

const handleViewArrear = (item) => {
  if (!item?.invoiceNo) return;
  const invoice = invoiceStore.invoices.find(
    (inv) => inv.invoiceNo === item.invoiceNo,
  );
  if (invoice?.id) {
    router.push(`/invoices/${invoice.id}`);
  }
};

const modalMode = ref(null);
const selectedArrear = ref(null);

const followUpForm = reactive({
  note: "",
  nextDate: "",
});

const openModal = (mode, item = null) => {
  modalMode.value = mode;
  selectedArrear.value = item;

  if (mode === "followup") {
    followUpForm.note = "";
    followUpForm.nextDate = "";
  }

  nextTick(() => getModal().show());
};

const openReminderModal = () => {
  openModal("reminder");
};

const openFollowUpModal = (item) => {
  openModal("followup", item);
};

const confirmFollowUp = () => {
  const item = selectedArrear.value;
  getModal().hide();

  setTimeout(() => {
    window.alert(
      `บันทึกติดตาม: ${item?.residentName || "-"}\n` +
        `นัดถัดไป: ${followUpForm.nextDate || "-"}\n` +
        `หมายเหตุ: ${followUpForm.note || "-"}`,
    );
  }, 300);
};

const handleFollowUp = (item) => {
  openFollowUpModal(item);
};
</script>
<template>
  <MainLayout>
    <div class="container-fluid px-0">
      <PageHeader
        title="รายการค้างชำระ"
        subtitle="ติดตามยอดค้างชำระค่าส่วนกลาง แยกตามอายุหนี้ และวางแผนการติดตามลูกบ้าน"
        button-text="ส่งแจ้งเตือน"
        button-icon="bi-bell-fill"
        button-color="warning"
        @action="openReminderModal"
      />

      <div class="row g-4 mb-4">
        <div class="col-md-6 col-xl-3">
          <ArrearsStatCard
            title="ค้างชำระทั้งหมด"
            :value="arrearsStore.totalCases.toLocaleString('th-TH')"
            subtitle="จำนวนบิลที่ยังมียอดคงเหลือ"
            icon="bi-exclamation-diamond"
            color="primary"
          />
        </div>
        <div class="col-md-6 col-xl-3">
          <ArrearsStatCard
            title="ยอดค้างรวม"
            :value="formatCurrency(arrearsStore.totalOutstanding)"
            subtitle="ยอดรวมที่ยังไม่ได้รับชำระ"
            icon="bi-cash-stack"
            color="danger"
          />
        </div>
        <div class="col-md-6 col-xl-3">
          <ArrearsStatCard
            title="เคสเร่งด่วน"
            :value="arrearsStore.urgentCases.toLocaleString('th-TH')"
            subtitle="อายุหนี้มากกว่า 60 วัน"
            icon="bi-alarm"
            color="warning"
          />
        </div>
        <div class="col-md-6 col-xl-3">
          <ArrearsStatCard
            title="ส่งต่อแล้ว"
            :value="arrearsStore.escalatedCases.toLocaleString('th-TH')"
            subtitle="เคสที่ส่งต่อผู้บริหาร/กฎหมาย"
            icon="bi-arrow-up-right-circle"
            color="dark"
          />
        </div>
      </div>

      <ArrearsFilterBar
        :search="search"
        :aging-group="agingGroup"
        :contact-status="contactStatus"
        :aging-group-options="arrearsStore.agingGroupOptions"
        :contact-status-options="arrearsStore.contactStatusOptions"
        @update:search="search = $event"
        @update:agingGroup="agingGroup = $event"
        @update:contactStatus="contactStatus = $event"
        @reset="arrearsStore.resetFilters()"
      />

      <div class="row g-4 mb-4">
        <div class="col-12">
          <AgingSummaryCard :summary="arrearsStore.summaryByAging" />
        </div>
      </div>

      <ArrearsTable
        :items="arrearsStore.filteredArrears"
        @view="handleViewArrear"
        @follow-up="handleFollowUp"
      />
    </div>

    <!-- ✅ Modal ยืนยันการส่งแจ้งเตือน -->
    <div
      class="modal fade"
      id="reminderModal"
      tabindex="-1"
      ref="reminderModalEl"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rounded-4 border-0 shadow">
          <div class="modal-header border-0 pb-0 px-4 pt-4">
            <h5 class="modal-title fw-bold">
              <i class="bi bi-bell-fill me-1 text-warning"></i
              >ส่งแจ้งเตือนการค้างชำระ
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>

          <div class="modal-body px-4 py-3">
            <div v-if="modalMode === 'reminder'">
              <p class="text-muted mb-3">
                ระบบจะส่งแจ้งเตือนไปยังลูกบ้านที่มียอดค้างชำระทั้งหมด
              </p>

              <div
                class="alert alert-warning rounded-3 d-flex gap-2 align-items-start mb-3"
              >
                <i
                  class="bi bi-exclamation-triangle-fill flex-shrink-0 mt-1"
                ></i>
                <div>
                  พบรายการค้างชำระ
                  <strong>{{ arrearsStore.totalCases }}</strong> รายการ รวมยอด
                  <strong>{{
                    formatCurrency(arrearsStore.totalOutstanding)
                  }}</strong>
                </div>
              </div>

              <div class="vstack gap-2">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="reminderForm.includeSMS"
                    id="includeSMS"
                  />
                  <label class="form-check-label" for="includeSMS">
                    <i class="bi bi-phone me-1 text-success"></i> ส่ง SMS
                    แจ้งเตือน
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="reminderForm.includeEmail"
                    id="includeEmail"
                  />
                  <label class="form-check-label" for="includeEmail">
                    <i class="bi bi-envelope me-1 text-primary"></i> ส่ง Email
                    แจ้งเตือน
                  </label>
                </div>
              </div>
            </div>
            <!-- ✅ เพิ่ม: Follow-up form (แสดงเมื่อ modalMode === 'followup') -->
            <div v-if="modalMode === 'followup'">
              <p class="text-muted mb-3">บันทึกการติดตามลูกหนี้</p>

              <div class="alert alert-warning rounded-3 mb-3">
                <div class="fw-semibold">
                  {{ selectedArrear?.residentName || "-" }}
                </div>
                <div class="small text-muted">
                  ใบแจ้งหนี้:
                  <strong>{{ selectedArrear?.invoiceNo || "-" }}</strong> •
                  ยอดค้าง:
                  <strong>{{
                    formatCurrency(selectedArrear?.totalOutstanding || 0)
                  }}</strong>
                  • ค่าปรับ:
                  <strong>{{
                    formatCurrency(selectedArrear?.penaltyAmount || 0)
                  }}</strong>
                </div>
              </div>

              <div class="vstack gap-3">
                <div>
                  <label class="form-label fw-semibold"
                    >นัดติดตามครั้งถัดไป</label
                  >
                  <input
                    type="date"
                    class="form-control"
                    v-model="followUpForm.nextDate"
                  />
                </div>

                <div>
                  <label class="form-label fw-semibold">หมายเหตุ</label>
                  <textarea
                    class="form-control"
                    rows="3"
                    v-model="followUpForm.note"
                    placeholder="สรุปการติดตาม/เงื่อนไข/วันที่นัดชำระ..."
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer border-0 px-4 pb-4 pt-2 gap-2">
            <button
              type="button"
              class="btn btn-danger border rounded-3 px-4"
              data-bs-dismiss="modal"
            >
              <i class="bi bi-x-lg me-1"></i>ยกเลิก
            </button>

            <button
              v-if="modalMode === 'followup'"
              type="button"
              class="btn btn-success rounded-3 px-4"
              @click="confirmFollowUp"
            >
              <i class="bi bi-check2-circle me-1"></i>บันทึกการติดตาม
            </button>

            <button
              v-if="modalMode === 'reminder'"
              type="button"
              class="btn btn-warning rounded-3 px-4"
              :disabled="!reminderForm.includeSMS && !reminderForm.includeEmail"
              @click="confirmSendReminder"
            >
              <i class="bi bi-bell me-1"></i>ส่งแจ้งเตือน
            </button>
          </div>
        </div>
      </div>
    </div>
  </MainLayout>
</template>
